/**
 * Road Racer Game
 * A top-down endless racer where the player dodges obstacles on a 3-lane road.
 */

import { getHighScores, type HighScoreEntry } from '../state/highscore'

// ============================================================================
// Types
// ============================================================================

/** Callback function called when game ends */
export type GameOverCallback = (score: number) => void

type Lane = 0 | 1 | 2 // Left, Center, Right

type ObstacleType = 'car' | 'truck' | 'barrier' | 'oil_slick' | 'cone'

interface Car {
  lane: Lane
  x: number
  y: number
  width: number
  height: number
}

interface Obstacle {
  type: ObstacleType
  lane: Lane
  x: number
  y: number
  width: number
  height: number
  passed: boolean // Whether player has passed this obstacle (for scoring)
}

interface GameState {
  car: Car
  obstacles: Obstacle[]
  score: number
  distance: number
  gameOver: boolean
  paused: boolean
  started: boolean
  canvasWidth: number
  canvasHeight: number
  lastObstacleSpawn: number // Distance at last spawn
  roadOffset: number // For scrolling road lines
}

interface InputState {
  left: boolean
  right: boolean
  leftPressed: boolean // For single-press lane changes
  rightPressed: boolean
}

// ============================================================================
// Constants
// ============================================================================

const LANE_COUNT = 3
const ROAD_WIDTH_RATIO = 0.6 // Road takes 60% of canvas width
const CAR_WIDTH = 40
const CAR_HEIGHT = 70
const CAR_Y_OFFSET = 100 // Distance from bottom of screen

const BASE_SCROLL_SPEED = 4
const SPEED_INCREMENT = 0.5
const SPEED_INCREASE_DISTANCE = 500 // Increase speed every 500 distance
const MAX_SPEED_MULTIPLIER = 3

// Convert pixels scrolled to realistic meters (1 pixel ≈ 0.15m feels right for the scale)
const PIXELS_TO_METERS = 0.15

const BASE_SPAWN_DISTANCE = 300 // Minimum distance between spawns (in pixels)
const MIN_SPAWN_DISTANCE = 150 // At max difficulty
const SPAWN_DISTANCE_DECREASE = 10 // Decrease spawn distance every 500 units

const OBSTACLE_CONFIGS: Record<
  ObstacleType,
  { width: number; height: number; points: number; minDistance: number }
> = {
  car: { width: 36, height: 60, points: 10, minDistance: 0 },
  truck: { width: 40, height: 90, points: 15, minDistance: 300 },
  barrier: { width: 50, height: 20, points: 10, minDistance: 0 },
  oil_slick: { width: 35, height: 35, points: 20, minDistance: 500 },
  cone: { width: 20, height: 20, points: 5, minDistance: 0 },
}

/** Game version */
export const RACER_VERSION = '1.0.0'

// ============================================================================
// Game Logic (Exported for Testing)
// ============================================================================

/**
 * Calculate the road boundaries based on canvas width
 */
export function getRoadBounds(canvasWidth: number): {
  left: number
  right: number
  laneWidth: number
} {
  const roadWidth = canvasWidth * ROAD_WIDTH_RATIO
  const left = (canvasWidth - roadWidth) / 2
  const right = left + roadWidth
  const laneWidth = roadWidth / LANE_COUNT

  return { left, right, laneWidth }
}

/**
 * Calculate the X position for a given lane
 */
export function getLaneX(lane: Lane, canvasWidth: number): number {
  const { left, laneWidth } = getRoadBounds(canvasWidth)
  return left + laneWidth * lane + laneWidth / 2
}

/**
 * Create the player's car
 */
export function createCar(canvasWidth: number, canvasHeight: number): Car {
  const lane: Lane = 1 // Start in center lane
  return {
    lane,
    x: getLaneX(lane, canvasWidth),
    y: canvasHeight - CAR_Y_OFFSET,
    width: CAR_WIDTH,
    height: CAR_HEIGHT,
  }
}

/**
 * Move the car to a different lane
 */
export function moveCar(car: Car, direction: -1 | 0 | 1, canvasWidth: number): Car {
  if (direction === 0) return car

  const newLane = Math.max(0, Math.min(LANE_COUNT - 1, car.lane + direction)) as Lane

  if (newLane === car.lane) return car

  return {
    ...car,
    lane: newLane,
    x: getLaneX(newLane, canvasWidth),
  }
}

/**
 * Create an obstacle of the specified type
 */
export function createObstacle(
  type: ObstacleType,
  lane: Lane,
  canvasWidth: number,
  y: number = -100
): Obstacle {
  const config = OBSTACLE_CONFIGS[type]
  return {
    type,
    lane,
    x: getLaneX(lane, canvasWidth),
    y,
    width: config.width,
    height: config.height,
    passed: false,
  }
}

/**
 * Get available obstacle types based on distance traveled
 */
export function getAvailableObstacleTypes(distance: number): ObstacleType[] {
  return (Object.entries(OBSTACLE_CONFIGS) as [ObstacleType, typeof OBSTACLE_CONFIGS.car][])
    .filter(([, config]) => distance >= config.minDistance)
    .map(([type]) => type)
}

/**
 * Spawn a random obstacle
 */
export function spawnRandomObstacle(
  canvasWidth: number,
  distance: number,
  existingObstacles: Obstacle[]
): Obstacle | null {
  const availableTypes = getAvailableObstacleTypes(distance)
  if (availableTypes.length === 0) return null

  // Pick random type
  const type = availableTypes[Math.floor(Math.random() * availableTypes.length)]

  // Pick random lane, avoiding lanes with obstacles near the top
  const occupiedLanes = new Set(existingObstacles.filter((o) => o.y < 100).map((o) => o.lane))

  const availableLanes = ([0, 1, 2] as Lane[]).filter((l) => !occupiedLanes.has(l))

  if (availableLanes.length === 0) return null

  const lane = availableLanes[Math.floor(Math.random() * availableLanes.length)]

  return createObstacle(type, lane, canvasWidth)
}

/**
 * Check collision between car and obstacle using AABB
 */
export function checkCollision(car: Car, obstacle: Obstacle): boolean {
  const carLeft = car.x - car.width / 2
  const carRight = car.x + car.width / 2
  const carTop = car.y - car.height / 2
  const carBottom = car.y + car.height / 2

  const obsLeft = obstacle.x - obstacle.width / 2
  const obsRight = obstacle.x + obstacle.width / 2
  const obsTop = obstacle.y - obstacle.height / 2
  const obsBottom = obstacle.y + obstacle.height / 2

  return carLeft < obsRight && carRight > obsLeft && carTop < obsBottom && carBottom > obsTop
}

/**
 * Calculate speed level based on distance
 */
export function getSpeedLevel(distance: number): number {
  return 1 + Math.floor(distance / SPEED_INCREASE_DISTANCE)
}

/**
 * Calculate current scroll speed based on distance
 */
export function getScrollSpeed(distance: number): number {
  const speedLevel = getSpeedLevel(distance)
  const multiplier = Math.min(
    1 + (speedLevel - 1) * (SPEED_INCREMENT / BASE_SCROLL_SPEED),
    MAX_SPEED_MULTIPLIER
  )
  return BASE_SCROLL_SPEED * multiplier
}

/**
 * Calculate spawn distance (minimum distance between obstacles)
 */
export function getSpawnDistance(distance: number): number {
  const reductions = Math.floor(distance / SPEED_INCREASE_DISTANCE)
  return Math.max(MIN_SPAWN_DISTANCE, BASE_SPAWN_DISTANCE - reductions * SPAWN_DISTANCE_DECREASE)
}

/**
 * Convert internal distance units (pixels) to display meters
 */
export function getDisplayDistance(distance: number): number {
  return Math.floor(distance * PIXELS_TO_METERS)
}

/**
 * Update obstacles: move them down, check if passed, remove off-screen
 */
export function updateObstacles(
  obstacles: Obstacle[],
  car: Car,
  scrollSpeed: number,
  canvasHeight: number
): { obstacles: Obstacle[]; pointsEarned: number } {
  let pointsEarned = 0

  const updatedObstacles = obstacles
    .map((obstacle) => {
      const newY = obstacle.y + scrollSpeed
      const passed = !obstacle.passed && newY > car.y + car.height / 2

      if (passed) {
        pointsEarned += OBSTACLE_CONFIGS[obstacle.type].points
      }

      return {
        ...obstacle,
        y: newY,
        passed: obstacle.passed || passed,
      }
    })
    .filter((obstacle) => obstacle.y - obstacle.height / 2 < canvasHeight + 50)

  return { obstacles: updatedObstacles, pointsEarned }
}

/**
 * Create the initial game state
 */
export function createGameState(canvasWidth: number, canvasHeight: number): GameState {
  return {
    car: createCar(canvasWidth, canvasHeight),
    obstacles: [],
    score: 0,
    distance: 0,
    gameOver: false,
    paused: false,
    started: false,
    canvasWidth,
    canvasHeight,
    lastObstacleSpawn: 0,
    roadOffset: 0,
  }
}

/**
 * Update the game state for one frame
 */
export function updateGame(state: GameState, input: InputState): GameState {
  if (state.gameOver || state.paused || !state.started) {
    return state
  }

  const scrollSpeed = getScrollSpeed(state.distance)

  // Handle lane changes (single press)
  let direction: -1 | 0 | 1 = 0
  if (input.leftPressed) direction = -1
  if (input.rightPressed) direction = 1

  const newCar = moveCar(state.car, direction, state.canvasWidth)

  // Update distance
  const newDistance = state.distance + scrollSpeed

  // Update road offset for scrolling effect
  const newRoadOffset = (state.roadOffset + scrollSpeed) % 40

  // Spawn new obstacles
  let newObstacles = [...state.obstacles]
  const spawnDistance = getSpawnDistance(state.distance)

  if (newDistance - state.lastObstacleSpawn >= spawnDistance) {
    const newObstacle = spawnRandomObstacle(state.canvasWidth, state.distance, newObstacles)
    if (newObstacle) {
      newObstacles.push(newObstacle)
    }
  }

  // Update obstacles
  const { obstacles: updatedObstacles, pointsEarned } = updateObstacles(
    newObstacles,
    newCar,
    scrollSpeed,
    state.canvasHeight
  )
  newObstacles = updatedObstacles

  // Check collisions
  let gameOver = false
  for (const obstacle of newObstacles) {
    if (checkCollision(newCar, obstacle)) {
      gameOver = true
      break
    }
  }

  // Calculate new score
  const newScore = state.score + pointsEarned

  return {
    ...state,
    car: newCar,
    obstacles: newObstacles,
    score: newScore,
    distance: newDistance,
    gameOver,
    roadOffset: newRoadOffset,
    lastObstacleSpawn:
      newDistance - state.lastObstacleSpawn >= spawnDistance
        ? newDistance
        : state.lastObstacleSpawn,
  }
}

// ============================================================================
// Rendering
// ============================================================================

function drawRoad(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  roadOffset: number
): void {
  const { left, right, laneWidth } = getRoadBounds(canvasWidth)

  // Road background
  ctx.fillStyle = '#2a2a2a'
  ctx.fillRect(left, 0, right - left, canvasHeight)

  // Road edges (white lines)
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(left, 0)
  ctx.lineTo(left, canvasHeight)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(right, 0)
  ctx.lineTo(right, canvasHeight)
  ctx.stroke()

  // Lane dividers (dashed lines)
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  ctx.setLineDash([20, 20])

  for (let i = 1; i < LANE_COUNT; i++) {
    const x = left + laneWidth * i
    ctx.beginPath()
    ctx.moveTo(x, -40 + roadOffset)
    ctx.lineTo(x, canvasHeight)
    ctx.stroke()
  }

  ctx.setLineDash([])

  // Grass/shoulder on sides
  ctx.fillStyle = '#1a472a'
  ctx.fillRect(0, 0, left, canvasHeight)
  ctx.fillRect(right, 0, canvasWidth - right, canvasHeight)
}

function drawCar(ctx: CanvasRenderingContext2D, car: Car): void {
  const x = car.x
  const y = car.y

  // Car body
  ctx.fillStyle = '#06b6d4' // Cyan (arcade theme)
  ctx.beginPath()
  ctx.roundRect(x - car.width / 2, y - car.height / 2, car.width, car.height, 8)
  ctx.fill()

  // Windshield
  ctx.fillStyle = '#0891b2'
  ctx.fillRect(x - car.width / 2 + 6, y - car.height / 2 + 10, car.width - 12, 15)

  // Rear window
  ctx.fillRect(x - car.width / 2 + 6, y + car.height / 2 - 20, car.width - 12, 10)

  // Wheels
  ctx.fillStyle = '#1f2937'
  // Front left
  ctx.fillRect(x - car.width / 2 - 4, y - car.height / 2 + 8, 6, 15)
  // Front right
  ctx.fillRect(x + car.width / 2 - 2, y - car.height / 2 + 8, 6, 15)
  // Rear left
  ctx.fillRect(x - car.width / 2 - 4, y + car.height / 2 - 23, 6, 15)
  // Rear right
  ctx.fillRect(x + car.width / 2 - 2, y + car.height / 2 - 23, 6, 15)
}

function drawObstacle(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
  const x = obstacle.x
  const y = obstacle.y
  const w = obstacle.width
  const h = obstacle.height

  switch (obstacle.type) {
    case 'car':
      // Red car
      ctx.fillStyle = '#ef4444'
      ctx.beginPath()
      ctx.roundRect(x - w / 2, y - h / 2, w, h, 6)
      ctx.fill()
      // Windshield
      ctx.fillStyle = '#b91c1c'
      ctx.fillRect(x - w / 2 + 5, y - h / 2 + 8, w - 10, 12)
      break

    case 'truck':
      // Blue/gray truck
      ctx.fillStyle = '#3b82f6'
      ctx.beginPath()
      ctx.roundRect(x - w / 2, y - h / 2, w, h, 4)
      ctx.fill()
      // Cab
      ctx.fillStyle = '#1e40af'
      ctx.fillRect(x - w / 2 + 4, y + h / 2 - 25, w - 8, 20)
      break

    case 'barrier':
      // Yellow/black striped barrier
      ctx.fillStyle = '#fbbf24'
      ctx.fillRect(x - w / 2, y - h / 2, w, h)
      // Black stripes
      ctx.fillStyle = '#1f2937'
      for (let i = 0; i < 4; i++) {
        ctx.fillRect(x - w / 2 + i * 14, y - h / 2, 7, h)
      }
      break

    case 'oil_slick':
      // Dark oil puddle
      ctx.fillStyle = '#4c1d95'
      ctx.beginPath()
      ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2)
      ctx.fill()
      // Shine
      ctx.fillStyle = '#7c3aed'
      ctx.beginPath()
      ctx.ellipse(x - 5, y - 5, w / 4, h / 4, 0, 0, Math.PI * 2)
      ctx.fill()
      break

    case 'cone':
      // Orange traffic cone
      ctx.fillStyle = '#f97316'
      ctx.beginPath()
      ctx.moveTo(x, y - h / 2)
      ctx.lineTo(x + w / 2, y + h / 2)
      ctx.lineTo(x - w / 2, y + h / 2)
      ctx.closePath()
      ctx.fill()
      // White stripe
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(x - w / 4, y, w / 2, 4)
      break
  }
}

function drawUI(
  ctx: CanvasRenderingContext2D,
  score: number,
  distance: number,
  speedLevel: number,
  canvasWidth: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.textAlign = 'left'
  ctx.fillText(`SCORE: ${score}`, 20, 30)

  // Distance below score (converted to realistic meters)
  ctx.fillStyle = '#9ca3af' // Gray
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText(`${getDisplayDistance(distance)}m`, 20, 50)

  ctx.fillStyle = '#f97316' // Orange for speed
  ctx.textAlign = 'right'
  ctx.fillText(`SPEED: ${speedLevel}x`, canvasWidth - 20, 30)
}

function drawStartScreen(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  highScores: HighScoreEntry[]
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '36px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('ROAD RACER', canvasWidth / 2, canvasHeight / 2 - 120)

  ctx.fillStyle = '#ffffff'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Dodge obstacles on the road!', canvasWidth / 2, canvasHeight / 2 - 70)

  // Show high scores if available
  if (highScores.length > 0) {
    ctx.fillStyle = '#f97316' // orange
    ctx.font = '10px "Press Start 2P", monospace'
    ctx.fillText('HIGH SCORES', canvasWidth / 2, canvasHeight / 2 - 35)

    ctx.fillStyle = '#9ca3af' // gray
    ctx.font = '9px "Press Start 2P", monospace'
    const topScores = highScores.slice(0, 3)
    topScores.forEach((entry, index) => {
      const y = canvasHeight / 2 - 10 + index * 18
      ctx.fillText(
        `${index + 1}. ${entry.name}  ${entry.score.toLocaleString()}`,
        canvasWidth / 2,
        y
      )
    })
  }

  const controlsY = highScores.length > 0 ? canvasHeight / 2 + 55 : canvasHeight / 2 + 20

  ctx.fillStyle = '#22c55e' // Green
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('CONTROLS', canvasWidth / 2, controlsY)

  ctx.fillStyle = '#9ca3af' // Gray
  ctx.font = '10px "Press Start 2P", monospace'
  ctx.fillText('Arrow Left/Right or A/D - Change Lane', canvasWidth / 2, controlsY + 25)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Press SPACE to start', canvasWidth / 2, controlsY + 70)
}

function drawGameOver(
  ctx: CanvasRenderingContext2D,
  score: number,
  distance: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#ef4444' // Red
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('CRASH!', canvasWidth / 2, canvasHeight / 2 - 60)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`SCORE: ${score}`, canvasWidth / 2, canvasHeight / 2)

  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText(`DISTANCE: ${getDisplayDistance(distance)}m`, canvasWidth / 2, canvasHeight / 2 + 30)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 70)
}

// ============================================================================
// Main Game Loop
// ============================================================================

/**
 * Initialize and run the Road Racer game
 * @param container The HTML element to render the game in
 * @param onGameOver Optional callback called when game ends with the final score
 * @returns Cleanup function to stop the game
 */
export function loadRacer(container: HTMLElement, onGameOver?: GameOverCallback): () => void {
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  container.innerHTML = ''
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get canvas context')
    return (): void => {}
  }

  // Store reference to avoid null checks in nested functions
  const context: CanvasRenderingContext2D = ctx

  // Resize canvas to match container
  function resizeCanvas(): void {
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
  }
  resizeCanvas()

  const resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(container)

  // Initialize game state
  let state = createGameState(canvas.width, canvas.height)

  // Input state
  const input: InputState = {
    left: false,
    right: false,
    leftPressed: false,
    rightPressed: false,
  }

  let running = true

  // Track if game over callback has been called
  let gameOverCallbackFired = false

  // Input handlers
  function handleKeyDown(e: KeyboardEvent): void {
    if (state.gameOver) {
      if (e.key === 'Enter') {
        // Restart game
        state = createGameState(canvas.width, canvas.height)
        gameOverCallbackFired = false
      }
      return
    }

    if (!state.started) {
      if (e.key === ' ') {
        state.started = true
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        if (!input.left) {
          input.left = true
          input.leftPressed = true
        }
        e.preventDefault()
        break
      case 'ArrowRight':
      case 'd':
        if (!input.right) {
          input.right = true
          input.rightPressed = true
        }
        e.preventDefault()
        break
    }
  }

  function handleKeyUp(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        input.left = false
        break
      case 'ArrowRight':
      case 'd':
        input.right = false
        break
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)

  // Game loop
  function gameLoop(): void {
    if (!running) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    context.fillStyle = '#0a0a0a'
    context.fillRect(0, 0, width, height)

    // Draw road (always visible)
    drawRoad(context, width, height, state.roadOffset)

    // Show start screen if game hasn't started
    if (!state.started) {
      const highScores = getHighScores('racer')
      drawStartScreen(context, width, height, highScores)
      requestAnimationFrame(gameLoop)
      return
    }

    // Track previous state
    const previousGameOver = state.gameOver

    // Update game state
    state = updateGame(state, input)

    // Fire game over callback once when game ends
    if (state.gameOver && !previousGameOver && !gameOverCallbackFired && onGameOver) {
      gameOverCallbackFired = true
      onGameOver(state.score)
    }

    // Reset pressed flags after update
    input.leftPressed = false
    input.rightPressed = false

    // Draw obstacles
    for (const obstacle of state.obstacles) {
      drawObstacle(context, obstacle)
    }

    // Draw player car
    drawCar(context, state.car)

    // Draw UI
    const speedLevel = getSpeedLevel(state.distance)
    drawUI(context, state.score, state.distance, speedLevel, width)

    if (state.gameOver) {
      drawGameOver(context, state.score, state.distance, width, height)
    }

    requestAnimationFrame(gameLoop)
  }

  // Start the game loop
  gameLoop()

  // Return cleanup function
  return (): void => {
    running = false
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
    resizeObserver.disconnect()
    container.innerHTML = ''
  }
}
